/**
 * The wall plan's drawing over the scene, Lite's WallPlanOverlay: dimension rows, the selection and
 * the magnets' guides on a canvas over the scene canvas that takes no input, redrawn after every
 * scene frame (a pan, a zoom, a drag) and every document change. The drawing itself is
 * `render/plan/wallPlanOverlay.ts`.
 */
import { useEffect, useRef, type RefObject } from 'react';
import { store } from '../../state/store';
import type { SceneHandle } from '../Scene3D';
import { drawWallPlanOverlay } from '../../render/plan/wallPlanOverlay';

export function WallPlanOverlay({ scene }: { scene: RefObject<SceneHandle | null> }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let frame = 0;
    const draw = () => {
      frame = 0;
      const element = canvas.current;
      if (!element) return;
      const mapping = scene.current?.wallPlanFrame(), level = store.presentedLevel;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.round(element.clientWidth * dpr)), height = Math.max(1, Math.round(element.clientHeight * dpr));
      if (element.width !== width || element.height !== height) { element.width = width; element.height = height; }
      const context = element.getContext('2d');
      if (!context) return;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, element.clientWidth, element.clientHeight);
      const selection = store.get().selection;
      if (mapping && level) drawWallPlanOverlay(context, { ...mapping, width: element.clientWidth || mapping.width, viewHeight: element.clientHeight || mapping.viewHeight }, level, {
        opening: selection?.kind === 'opening' ? selection.id : null,
        wallSelected: selection?.kind === 'wall' && selection.id === mapping.wall.id,
        marks: scene.current?.wallPlanMarks() ?? null,
      });
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(draw); };
    const unsubscribeFrame = scene.current?.onFrame(schedule) ?? (() => {});
    const unsubscribeStore = store.subscribe(schedule);
    window.addEventListener('resize', schedule);
    schedule();
    return () => { if (frame) cancelAnimationFrame(frame); unsubscribeFrame(); unsubscribeStore(); window.removeEventListener('resize', schedule); };
  }, [scene]);
  return <canvas ref={canvas} className="wall-plan-overlay" aria-hidden="true" />;
}
